import React, { useEffect, useState } from "react";
import {
  SortingRule,
  useAsyncDebounce,
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

import { PrimaryButton } from "@/components/elements/Button";
import { TableFilters, TablePagination } from "@/components/elements/DataTable";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadContent,
  TableHeader,
  TableRow,
} from "@/components/elements/Table";
import { PlusIcon } from "@heroicons/react/20/solid";

import { DataSource, columns } from "./columns";
import { globalFilterFn, syncStateFilterFn } from "./filter-fns";

const filterTypes = {
  syncState: syncStateFilterFn,
};

const PAGE_SIZE = 20;

export function DataTable(props: {
  data: DataSource[];
  onRowClick: (
    row: DataSource,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
  ) => void;
  onCreateNew: () => void;
}) {
  const [sorting, setSorting] = useState<SortingRule<DataSource>[]>([
    {
      id: "createdAt",
      desc: true,
    },
  ]);

  const [localSearch, setLocalSearch] = useState<string>("");

  const {
    canNextPage,
    canPreviousPage,
    gotoPage,
    getTableBodyProps,
    getTableProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageCount,
    setGlobalFilter: setTableGlobalFilter,
    state: { sortBy, filters, pageIndex },
    allColumns,
  } = useTable<DataSource>(
    {
      columns,
      data: props.data,
      filterTypes,
      initialState: {
        sortBy: sorting,
        hiddenColumns: ["createdAt", "active", "connector", "syncState"],
        pageSize: PAGE_SIZE,
      },
      globalFilter: globalFilterFn,
      autoResetFilters: false,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    setSorting(sortBy);
  }, [sortBy]);

  const debouncedSetGlobal = useAsyncDebounce((val: string) => {
    setTableGlobalFilter(val || undefined);
  }, 300);

  function onSearchChange(searchTerm: string) {
    setLocalSearch(searchTerm);
    debouncedSetGlobal(searchTerm);
  }

  return (
    <div className="w-full">
      <div className="mb-6">
        <div className="flex flex-col-reverse gap-4 xl:flex-row">
          <TableFilters
            columns={allColumns}
            searchTerm={localSearch}
            onSearchChange={onSearchChange}
            onResetFilters={() => {
              onSearchChange("");
              allColumns.forEach((column) => {
                if (column.canFilter && column.setFilter) {
                  column.setFilter(undefined);
                }
              });
            }}
            isFiltered={filters.length > 0 || localSearch !== ""}
          />
          <div className="ml-auto self-end">
            <PrimaryButton
              icon={<PlusIcon className="w-4" />}
              onClick={() => props.onCreateNew()}
              size="sm"
              className="whitespace-nowrap"
            >
              Add Data Source
            </PrimaryButton>
          </div>
        </div>
      </div>
      <Table {...getTableProps()}>
        <TableHeader>
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    {...header.getHeaderProps({
                      style: {
                        maxWidth: header.maxWidth,
                        minWidth: header.minWidth,
                        width: header.width,
                      },
                    })}
                  >
                    <TableHeadContent
                      isSorted={header.isSorted}
                      isSortedDesc={header.isSortedDesc}
                      canSort={header.canSort}
                      {...header.getSortByToggleProps()}
                    >
                      {header.render("Header")}
                    </TableHeadContent>
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <TableRow
                {...row.getRowProps()}
                onClick={(e) => props.onRowClick(row.original, e)}
              >
                {row.cells.map((cell) => {
                  return (
                    <TableCell
                      {...cell.getCellProps({
                        style: {
                          maxWidth: cell.column.maxWidth,
                          minWidth: cell.column.minWidth,
                          width: cell.column.width,
                        },
                      })}
                    >
                      {cell.render("Cell")}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        className="mt-3"
        pageIndex={pageIndex}
        pageCount={pageCount}
        pageSize={PAGE_SIZE}
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
        onChangePage={gotoPage}
        totalRows={rows.length}
      />
    </div>
  );
}
